import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import Qualifications from "./Qualifications";
import { BriefcaseBusiness, CalendarDays, Clock, Coffee, MapPin } from "lucide-react";
import { useCompanyTimeFormat } from "../../../../../../../utils/hooks/company";
import { renderSectionsDuration } from "../../../../helpers/eventdisplay/renderSectionsDuration";
import { renderDuration } from "../../../../helpers/eventdisplay/renderDuration";
import { renderSettingsResourceLabel } from "../../../../../../../utils/helpers/settings";
import type { CalculatedSection } from "../types";

interface SectionDetailsProps {
    sections: CalculatedSection[];
    keepZone?: boolean;
}

function SectionDetails({ sections, keepZone }: SectionDetailsProps) {
    const { t } = useTranslation();
    const userTimezone = moment.tz.guess(true);
    const toDisplay = (date: any) => keepZone ? moment.parseZone(date) : moment.parseZone(date).tz(userTimezone);
    const companyTimeFormat = useCompanyTimeFormat();

    const { totalBreaksDuration, breaks } = useMemo(() => {
        const totalBreaksDuration = sections?.reduce((total: number, section) => {
            if (section.type === "break" && section.start && section.end) {
                const start = moment(section.start);
                const end = moment(section.end);

                if (start.isValid() && end.isValid()) {
                    const diff = end.diff(start, "seconds");
                    return total + (Number(diff) || 0);
                }
            }
            return total;
        }, 0);

        return {
            totalBreaksDuration,
            breaks: sections?.filter((section) => section?.type === "break")
        }
    }, [sections]);

    return (
        <div className="tw-border tw-rounded-lg shadow-xs tw-p-4">
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-1">
                <CalendarDays size={19} className="tw-text-primary" />
                <span className="tw-text-sm">
                    {toDisplay(sections?.[0]?.start).format("ddd, DD MMMM YYYY")}
                </span>
            </div>

            <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <Clock size={19} className="tw-text-primary" />
                    <span className="tw-text-sm">
                        {toDisplay(sections?.[0]?.start).format(companyTimeFormat)}
                        {" - "}
                        {toDisplay(sections?.[sections?.length - 1]?.end).format(companyTimeFormat)}
                    </span>
                </div>
                <div className="tw-bg-neutral-200 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-neutral-700 tw-font-semibold">
                    {renderSectionsDuration(
                        moment(sections?.[sections?.length - 1]?.end).diff(moment(sections?.[0]?.start), "seconds"),
                        sections
                    )}
                </div>
            </div>

            <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <Coffee size={19} className="tw-text-primary" />
                    <span className="tw-text-sm tw-flex tw-items-center tw-gap-1">
                        {t("breaks")}
                        <p className="tw-text-neutral-500">{`(${breaks?.length})`}</p>
                    </span>
                </div>
                <div className="tw-bg-amber-100 tw-text-sm tw-px-3 tw-py-1 tw-rounded tw-text-amber-900 tw-font-semibold">
                    {totalBreaksDuration > 0 ? renderDuration(totalBreaksDuration) : "0h"}
                </div>
            </div>

            <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <BriefcaseBusiness size={19} className="tw-text-primary" />
                    <span className="tw-text-sm">{t("job")}</span>
                </div>
                <div className="tw-bg-primary/20 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-primary tw-font-semibold">
                    {renderSettingsResourceLabel(sections?.[0]?.job)}
                </div>
            </div>

            <div className="tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <MapPin size={19} className="tw-text-primary" />
                    <span className="tw-text-sm">{t("location")}</span>
                </div>
                <div className="tw-bg-purple-200 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-purple-950 tw-font-semibold">
                    {renderSettingsResourceLabel(sections?.[0]?.location)}
                </div>
            </div>

            <Qualifications sections={sections} />
        </div>
    );
}

export default SectionDetails;
